import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {OrganizationUserController} from '../../../../api/organization/organization-user.controller';
import {SupportPlanTaskController} from '../../../../api/support/support-plan-task.controller';
import {SupportTicketController} from '../../../../api/support/support-ticket.controller';
import {useInitialLoad} from '../../../../hook/use-initial-load';
import {OrganizationUserModel} from '../../../../model/organization/organization-user.model';
import {SupportPlanTaskType} from '../../../../model/support/task/support-plan-task-type.enum';
import {
  Searchable,
  SearchableFilter,
} from '../../../../state/searchable-state';
import {LazyAutocompleteOption} from '../components/LazyAutocomplete';
import {CreateSupportPlanTaskContext} from './support-plan-page-state';
import CreateSupportPlanTaskReq = SupportPlanTaskController.CreateSupportPlanTaskReq;
import QuerySupportTicketsRes = SupportTicketController.QuerySupportTicketsRes;

export type SupportTaskCreateDrawerState = {
  translate: (key: string) => string
  formData: CreateSupportPlanTaskReq
  setFormData: (data: CreateSupportPlanTaskReq) => void
  ticketsData: QuerySupportTicketsRes | null
  fetchTicketsOptions: (page: number, search: string, pageSize: number) => Promise<LazyAutocompleteOption[]>;
  users: OrganizationUserModel[]

  handleSubmit: () => Promise<void>
}

export const useSupportTaskCreateDrawerState = (organizationId: string, supportPlanId: string, initialContext: CreateSupportPlanTaskContext | null): SupportTaskCreateDrawerState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()
  const buildInitialTaskType = (): SupportPlanTaskType => {
    const assignmentType = initialContext?.slot?.assignmentType

    if(assignmentType) {
      if(assignmentType === 'ANALYSIS') return SupportPlanTaskType.ANALYSIS
      if(assignmentType === 'CONSULTATION') return SupportPlanTaskType.CONSULTATION
      if(assignmentType === 'GENERAL') return SupportPlanTaskType.ANALYSIS
    }

    return SupportPlanTaskType.ANALYSIS
  }

  const buildInitialPlannedHours = (): number => {
    const startTime = initialContext?.slot?.startTime
    const endTime = initialContext?.slot?.endTime

    if (!startTime || !endTime) return 0;

    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);

    const startMinutes = sh * 60 + sm;
    const endMinutes = eh * 60 + em;

    const diffMinutes = endMinutes - startMinutes;
    if (diffMinutes <= 0) return 0;

    return Number((diffMinutes / 60).toFixed(2));
  }

  const [formData, setFormData] = useState<CreateSupportPlanTaskReq>({
    ticketId: null, ticketName: null, taskType: buildInitialTaskType(), title: '', description: '', users: [], meetingLink: null,
    startDate: initialContext?.startDate ?? null, endDate: initialContext?.endDate ?? null, startTime: initialContext?.slot?.startTime ?? null, endTime: initialContext?.slot?.endTime ?? null, plannedHours: buildInitialPlannedHours()
  })

  const [ticketsData, setTicketsData] = useState<QuerySupportTicketsRes | null>(null)
  const users = useInitialLoad(async () => await fetchOrganizationUsers(organizationId)) ?? []

  return {
    users: users,
    ticketsData: ticketsData,
    fetchTicketsOptions: async (page, search, pageSize) => {
      const result = await fetchSupportTickets(organizationId, page, search, pageSize)
      setTicketsData(result)

      return result.items.map(it => ({id: it.id, name: it.title}))
    },
    formData: formData,
    setFormData: setFormData,
    handleSubmit: async () => {
      await SupportPlanTaskController.createSupportPlanTask(organizationId, supportPlanId, formData)
    },
    translate: (key) => apiTranslate(key) || t(key),
  }
}

const fetchSupportTickets = async (organizationId: string, page: number, search: string, pageSize: number): Promise<QuerySupportTicketsRes> => {
  const searchFilter: SearchableFilter[] = search != null && search !== '' ? [{
    column: "support_ticket.title",
    searchOperator: "ILIKE",
    values: [`%${search}%`]
  }] : []

  const organizationFilter: SearchableFilter[] = [{
    column: "support_ticket.organization_id",
    searchOperator: "IN",
    values: [organizationId]
  }]

  const searchable: Searchable = {
    fullTextSearch: null,
    filters: [],
    groupedFilters: [],
    filtersGroups: [
      {
        filterGroupType: 'AND',
        filters: [...searchFilter, ...organizationFilter]
      }
    ],
    ordering: [],
    page: page,
    pageSize: pageSize,
    availableValues: []
  };

  return (await SupportTicketController.querySupportTickets(searchable))
}

const fetchOrganizationUsers = async (organizationId: string): Promise<OrganizationUserModel[]> =>
  (await OrganizationUserController.queryOrganizationUsers(organizationId))

